(() => {
  const sectionSelector = '[data-search-page]';
  const filterToggleSelector = '[data-search-filter-toggle]';
  const filterIconSelector = '[data-search-filter-icon]';
  const filterPanelSelector = '[data-search-filter-panel]';
  const searchFormSelector = '[data-search-form]';
  const searchInputSelector = 'input[name="q"]';
  const sortSelectSelector = '[data-search-sort-select]';
  const intellitypeSelector = '[data-search-intellitype]';
  const intellitypeInputSelector = '[data-search-intellitype-input]';
  const intellitypeValueSelector = '[data-search-intellitype-value]';
  const intellitypeListSelector = '[data-search-intellitype-list]';
  const intellitypeOptionSelector = '[data-search-intellitype-option]';
  const intellitypeClearSelector = '[data-search-intellitype-clear]';
  const productCardSelector = '[data-search-product-card]';
  const productTriggerSelector = '[data-search-product-trigger]';
  const drawerSlotSelector = '[data-search-drawer-slot]';
  const drawerCloseSelector = '[data-search-drawer-close]';
  const drawerGalleryRootSelector = '[data-search-drawer-gallery]';
  const drawerGallerySelector = '[data-search-drawer-gallery-rotate]';
  const drawerGalleryImageSelector = '[data-search-drawer-gallery-image]';
  const drawerMediaQuery = window.matchMedia('(min-width: 1100px)');

  const wildcardSearchTerm = (value) => {
    const query = value.trim();

    if (!query || query.endsWith('*') || /[:"]/u.test(query)) return query;

    return `${query}*`;
  };

  const submitSearchForm = (form, input) => {
    const formData = new FormData(form);
    const params = new URLSearchParams(formData);
    const action = form.getAttribute('action') || window.location.pathname;

    const hasActiveFilters = [...params].some(([name, value]) => name.startsWith('filter.') && value.trim());
    const query = wildcardSearchTerm(input.value);
    params.set(input.name, query || (hasActiveFilters ? '*' : ''));
    window.location.href = `${action}?${params.toString()}`;
  };

  class SearchPage {
    constructor(section) {
      this.section = section;
      this.filterToggles = [...section.querySelectorAll(filterToggleSelector)];
      this.filterPanel = section.querySelector(filterPanelSelector);
      this.searchForm = section.querySelector(searchFormSelector);
      this.sortSelect = section.querySelector(sortSelectSelector);
      this.intellitypes = [...section.querySelectorAll(intellitypeSelector)];
      this.drawerSlot = section.querySelector(drawerSlotSelector);
      this.activeCard = null;
      this.activeTrigger = null;
      this.drawerRotationTimer = null;

      this.bind();
      if (drawerMediaQuery.addEventListener) {
        drawerMediaQuery.addEventListener('change', () => {
          if (!drawerMediaQuery.matches) this.closeDrawer();
        });
      }
    }

    bind() {
      this.section.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !this.activeTrigger) return;
        if (!this.drawerSlot || !this.drawerSlot.contains(event.target)) return;

        event.preventDefault();
        event.stopPropagation();
        this.closeDrawer({ restoreFocus: true });
      });

      if (this.filterToggles.length && this.filterPanel) {
        this.filterToggles.forEach((toggle) => {
          toggle.addEventListener('click', () => this.toggleFilters());
        });
      }

      if (this.sortSelect && this.searchForm) {
        this.sortSelect.addEventListener('change', () => {
          if (typeof this.searchForm.requestSubmit === 'function') {
            this.searchForm.requestSubmit();
          } else {
            this.searchForm.submit();
          }
        });
      }

      if (this.searchForm) {
        this.searchForm.addEventListener('submit', (event) => {
          const input = this.searchForm.querySelector(searchInputSelector);
          if (!input) return;

          event.preventDefault();
          submitSearchForm(this.searchForm, input);
        });
      }

      this.section.querySelectorAll(productTriggerSelector).forEach((trigger) => {
        trigger.addEventListener('click', (event) => this.onProductTrigger(event, trigger));
      });

      this.intellitypes.forEach((intellitype) => this.bindIntellitype(intellitype));
    }

    toggleFilters() {
      const isExpanded = this.filterToggles[0].getAttribute('aria-expanded') === 'true';
      const nextExpanded = !isExpanded;

      this.filterToggles.forEach((toggle) => {
        toggle.setAttribute('aria-expanded', String(nextExpanded));
        this.animateFilterIcon(toggle, nextExpanded);
      });
      this.filterPanel.hidden = isExpanded;
    }

    animateFilterIcon(toggle, isExpanded) {
      const icon = toggle.querySelector(filterIconSelector);
      if (!icon) return;

      icon.classList.remove('is-spinning');
      void icon.offsetWidth;
      icon.classList.add('is-spinning');

      window.setTimeout(() => {
        icon.textContent = isExpanded ? '−' : '+';
        icon.classList.toggle('is-open', isExpanded);
        icon.classList.remove('is-spinning');
      }, 130);
    }

    bindIntellitype(intellitype) {
      const input = intellitype.querySelector(intellitypeInputSelector);
      const hiddenInput = intellitype.querySelector(intellitypeValueSelector);
      const list = intellitype.querySelector(intellitypeListSelector);
      const options = [...intellitype.querySelectorAll(intellitypeOptionSelector)];
      const clearButton = intellitype.querySelector(intellitypeClearSelector);

      if (!input || !hiddenInput || !list || !options.length) return;

      const closeList = () => {
        list.hidden = true;
        input.setAttribute('aria-expanded', 'false');
      };

      const openList = () => {
        list.hidden = false;
        input.setAttribute('aria-expanded', 'true');
      };

      const filterOptions = () => {
        const term = input.value.trim().toLowerCase();
        let visibleCount = 0;

        options.forEach((option) => {
          const label = option.dataset.label || option.textContent || '';
          const isVisible = label.toLowerCase().includes(term);
          option.hidden = !isVisible;
          if (isVisible) visibleCount += 1;
        });

        if (visibleCount > 0) {
          openList();
        } else {
          closeList();
        }
      };

      const selectOption = (option) => {
        hiddenInput.value = option.dataset.value || '';
        input.value = option.dataset.label || '';
        options.forEach((item) => item.setAttribute('aria-selected', String(item === option)));
        if (clearButton) clearButton.hidden = hiddenInput.value === '';
        closeList();
      };

      input.addEventListener('focus', filterOptions);

      input.addEventListener('input', () => {
        hiddenInput.value = '';
        options.forEach((option) => option.setAttribute('aria-selected', 'false'));
        if (clearButton) clearButton.hidden = input.value.trim() === '';
        filterOptions();
      });

      input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          closeList();
          return;
        }

        if (event.key !== 'Enter') return;

        const firstVisibleOption = options.find((option) => !option.hidden);
        if (!firstVisibleOption || list.hidden) return;

        event.preventDefault();
        selectOption(firstVisibleOption);
      });

      options.forEach((option) => {
        option.addEventListener('click', () => selectOption(option));
      });

      if (clearButton) {
        clearButton.addEventListener('click', () => {
          hiddenInput.value = '';
          input.value = '';
          clearButton.hidden = true;
          options.forEach((option) => {
            option.hidden = false;
            option.setAttribute('aria-selected', 'false');
          });
          input.focus();
          openList();
        });
      }

      document.addEventListener('click', (event) => {
        if (!intellitype.contains(event.target)) closeList();
      });
    }

    onProductTrigger(event, trigger) {
      if (!drawerMediaQuery.matches) return;

      const templateId = trigger.getAttribute('aria-controls');
      const template = templateId && document.getElementById(templateId);

      if (!template || !this.drawerSlot) return;

      event.preventDefault();

      const card = trigger.closest(productCardSelector);
      const rowEndCard = this.findRowEndCard(card);
      const slotParent = this.drawerSlot.parentElement;

      this.closeDrawer();

      if (rowEndCard && slotParent) {
        rowEndCard.insertAdjacentElement('afterend', this.drawerSlot);
      }

      this.drawerSlot.innerHTML = '';
      this.drawerSlot.appendChild(template.content.cloneNode(true));

      const drawer = this.drawerSlot.querySelector('[data-search-product-drawer]');
      const closeButton = this.drawerSlot.querySelector(drawerCloseSelector);

      if (drawer) {
        drawer.classList.add('is-opening');
        this.initDrawerGallery(drawer);
        window.requestAnimationFrame(() => this.alignDrawerInViewport(drawer));
      }

      if (card) {
        card.classList.add('is-active');
        this.activeCard = card;
      }

      this.section.querySelectorAll(productTriggerSelector).forEach((item) => {
        item.setAttribute('aria-expanded', item.getAttribute('aria-controls') === templateId ? 'true' : 'false');
      });

      if (closeButton) {
        closeButton.addEventListener('click', () => this.closeDrawer({ restoreFocus: true }));
        closeButton.focus({ preventScroll: true });
      }
      this.activeTrigger = trigger;
    }

    alignDrawerInViewport(drawer) {
      const bottomMargin = 40;
      const rect = drawer.getBoundingClientRect();
      const viewportBottom = window.innerHeight - bottomMargin;
      const isOffscreen = rect.top < 0 || rect.bottom > viewportBottom;

      if (!isOffscreen) return;

      const targetScrollY = Math.max(0, window.scrollY + rect.bottom - window.innerHeight + bottomMargin);

      window.scrollTo({
        top: targetScrollY,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
    }

    findRowEndCard(card) {
      if (!card) return null;

      const cards = [...this.section.querySelectorAll(productCardSelector)];
      const cardTop = card.offsetTop;
      const rowCards = cards.filter((item) => Math.abs(item.offsetTop - cardTop) < 4);

      return rowCards[rowCards.length - 1] || card;
    }

    closeDrawer({ restoreFocus = false } = {}) {
      const trigger = this.activeTrigger;
      this.stopDrawerGallery();

      if (this.drawerSlot) {
        this.drawerSlot.innerHTML = '';
      }

      if (this.activeCard) {
        this.activeCard.classList.remove('is-active');
      }

      this.activeCard = null;
      this.activeTrigger = null;

      this.section.querySelectorAll(productTriggerSelector).forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
      });

      if (restoreFocus && trigger && trigger.isConnected) {
        trigger.focus({ preventScroll: true });
      }
    }

    initDrawerGallery(drawer) {
      this.stopDrawerGallery();

      const galleryRoot = drawer.querySelector(drawerGalleryRootSelector);
      if (!galleryRoot) return;

      const images = [...galleryRoot.querySelectorAll(drawerGalleryImageSelector)];
      if (images.length < 2) return;

      galleryRoot.addEventListener('pointerenter', () => this.showNextDrawerImage(images));

      const rotatingGallery = drawer.querySelector(drawerGallerySelector);
      if (!rotatingGallery) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.drawerRotationTimer = window.setInterval(() => {
        this.showNextDrawerImage(images);
      }, 3200);
    }

    showNextDrawerImage(images) {
      if (!images.length) return;

      let activeIndex = images.findIndex((image) => !image.hidden);
      if (activeIndex < 0) activeIndex = 0;

      images[activeIndex].hidden = true;
      images[activeIndex].classList.remove('is-active');
      activeIndex = (activeIndex + 1) % images.length;
      images[activeIndex].hidden = false;
      images[activeIndex].classList.add('is-active');
    }

    stopDrawerGallery() {
      if (!this.drawerRotationTimer) return;

      window.clearInterval(this.drawerRotationTimer);
      this.drawerRotationTimer = null;
    }

  }

  const init = (scope = document) => {
    scope.querySelectorAll(sectionSelector).forEach((section) => {
      if (section.dataset.searchPageInitialized === 'true') return;

      section.dataset.searchPageInitialized = 'true';
      new SearchPage(section);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
